/**
 * UI Translation dictionary for ScoreCite reports.
 * All hardcoded labels, badges, tags, and buttons are translated here.
 */

const translations = {
  en: {
    // Section titles
    executive_summary: 'Executive Summary',
    // Security & performance module
    security: 'Security',
    security_grade: 'Grade',
    ssl_expires_in: 'SSL expires in',
    days: 'days',
    security_issues: 'Security issues to fix',
    fix: 'Fix',
    core_web_vitals: 'Core Web Vitals',
    cwv_desc: 'Real-world loading & interactivity performance measured by Google PageSpeed (mobile).',
    performance: 'Performance',
    broken_links: 'Broken Links',
    broken_links_found: 'We found',
    broken_links_pages: 'broken URL(s)',
    of_scanned: 'of scanned pages',
    server_errors: 'server error(s) (5xx)',
    business_overview: 'Business Overview',
    coverage: 'Coverage',
    content: 'Content',
    trust: 'Trust',
    seo: 'SEO',
    indexing: 'Indexing',
    ai_readiness: 'AI Readiness',
    contact: 'Contact',
    conversion_risk: 'Conversion Risk',
    action_plan: 'Action Plan',
    problems_and_fixes: 'Problems & Fixes',
    market_intelligence: 'Market Intelligence Strategy',
    customer_questions_ai: 'Customer Questions in AI',
    how_ai_sees_you: 'How AI Sees You',
    ready_to_deploy_briefs: 'Ready-to-Deploy Briefs',
    ready_to_deploy_brief: 'Ready-to-Deploy Brief',
    kpi_tracking: 'KPI Tracking',

    // Badges & tags
    critical: 'Critical',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
    found: 'Found',
    missing: 'Missing',
    auto_fix: 'auto-fix',

    // Category tags
    cat_content: 'Content',
    cat_trust: 'Trust',
    cat_ai: 'AI',
    cat_seo: 'SEO',
    cat_coverage: 'Coverage',
    cat_cro: 'CRO',
    cat_indexing: 'Indexing',
    cat_contact: 'Contact',

    // Labels
    impact: 'Impact',
    effort: 'Effort',
    acceptance_criteria: 'Acceptance Criteria',
    quick_wins: 'Quick Wins',
    next_30_days: 'Next 30 Days',
    priority_1: 'Priority 1 — Immediate, <1 week',
    priority_2: 'Priority 2 — Short-term, 1-2 weeks',
    priority_3: 'Priority 3 — Long-term, 1+ month',

    // Buttons
    copy: 'Copy',
    copied: 'Copied',
    show_solution: 'Show solution',
    hide_code: 'Hide code',
    how_to_deploy: 'How to deploy in 60 seconds',
    ai_prompt: 'AI Prompt',

    // GEO section
    ai_visibility_score: 'AI Visibility Score',
    questions_answered: 'questions answered',
    business_type: 'Business type',
    ai_cannot_find: 'AI cannot find an answer to this customer question on your website.',
    add_content_answering: 'Add content answering',
    why_ai_doesnt_cite: 'Why AI doesn\'t cite this page',
    ideal_keywords: 'Ideal Keywords',
    customer_questions: 'Customer Questions',

    // Sub-labels
    niche: 'Niche',
    location: 'Location',
    business_goal: 'Business Goal',
    primary_goal: 'Primary Goal',
    business_model: 'Business Model',
    score_reasoning: 'Score Reasoning',
    overall_assessment: 'Overall Assessment',
    top_performing_pages: 'Top Performing Pages',
    problem_pages: 'Problem Pages',
    detected_elements: 'Detected Elements',
    missing_elements: 'Missing Elements',
    business_risk: 'Business Risk',
    regional_assessment: 'Regional Assessment',
    on_page_assessment: 'On-Page Assessment',
    technical_assessment: 'Technical Assessment',
    structure_assessment: 'Structure Assessment',
    keyword_positioning: 'Keyword Positioning',
    expected_visibility: 'Expected Visibility Effect',
    internal_linking: 'Internal Linking',
    pages_scanned: 'pages scanned',
    unique_pages: 'unique pages',
    full_audit: 'full audit',
    pages: 'pages',

    // Fix types
    code_prompt_label: 'AI Prompt',
    code_html_label: 'HTML',
    code_text_label: 'Text',

    // Misc
    north_star: 'North Star',
    additional_metrics: 'Additional Metrics',

    // Problem fix display
    show_fix: 'Show fix',
    hide_fix: 'Hide fix',
    fix_available: 'Fix available',
    unlock_with_paid: 'unlock with a paid plan.',
    unlock: 'Unlock',
    code_label: 'code',

    // Contact labels
    email_label: 'Email',
    social_label: 'Social',
    phone_label: 'Phone',
    address_label: 'Address',
    opening_hours: 'Opening Hours',
    contact_form: 'Contact form available',

    // Trust & AI labels
    expertise_signals: 'Expertise signals',
    weaknesses_detected: 'Weaknesses detected',
    trust_detected: 'Trust elements detected',
    trust_missing: 'Trust elements missing',
    missing_ai_signals: 'Missing AI signals',
    citability_level: 'Citability level',
    schema_detected: 'Schema Detected',
    citability_strength: 'Citability Strength',
    geo_alignment: 'GEO/LLM Alignment',
    business_opportunity: 'Business Opportunity',

    // Indexing labels
    sitemap_label: 'Sitemap',
    robots_txt_label: 'Robots.txt',
    canonical_tags: 'Canonical Tags',
    meta_robots: 'Meta Robots',
    present: 'Present',
    not_detected: 'Not detected',
    ok_label: 'OK',
    issues_label: 'Issues',
    no_blocking_meta: 'No blocking meta robots tags detected.',

    // Stats labels
    pages_crawled: 'pages crawled and analyzed',
    content_gaps: 'content gaps detected',
    issues_detected: 'issues detected',
    missing_contact: 'missing contact elements',
    all_urls_analyzed: 'All URLs Analyzed',

    // Competitive
    competitive_analysis: 'Competitive Analysis',
    score_comparison: 'Score Comparison',
    where_you_win: 'Where You Win',
    where_competitors_lead: 'Where Competitors Lead',
    competitive_gaps: 'Competitive Gaps',
    suggested_pages: 'Suggested Pages to Create',
  },

  pl: {
    // Section titles
    executive_summary: 'Podsumowanie Wykonawcze',
    // Security & performance module
    security: 'Bezpieczeństwo',
    security_grade: 'Ocena',
    ssl_expires_in: 'Certyfikat SSL wygasa za',
    days: 'dni',
    security_issues: 'Problemy bezpieczeństwa do naprawy',
    fix: 'Naprawa',
    core_web_vitals: 'Core Web Vitals',
    cwv_desc: 'Rzeczywista wydajność ładowania i interaktywności mierzona przez Google PageSpeed (mobile).',
    performance: 'Wydajność',
    broken_links: 'Niedziałające linki',
    broken_links_found: 'Znaleziono',
    broken_links_pages: 'niedziałających adresów URL',
    of_scanned: 'skanowanych stron',
    server_errors: 'błędów serwera (5xx)',
    business_overview: 'Ogólny Przegląd Biznesu',
    coverage: 'Pokrycie',
    content: 'Zawartość',
    trust: 'Zaufanie',
    seo: 'SEO',
    indexing: 'Indeksowanie',
    ai_readiness: 'Gotowość na AI',
    contact: 'Kontakt',
    conversion_risk: 'Ryzyko Konwersji',
    action_plan: 'Plan Działania',
    problems_and_fixes: 'Problemy i Rozwiązania',
    market_intelligence: 'Strategia Wywiadu Rynkowego',
    customer_questions_ai: 'Pytania Klientów w AI',
    how_ai_sees_you: 'Jak AI Widzi Twoją Firmę',
    ready_to_deploy_briefs: 'Briefy Gotowe do Wdrożenia',
    ready_to_deploy_brief: 'Brief Gotowy do Wdrożenia',
    kpi_tracking: 'Śledzenie KPI',

    // Badges & tags
    critical: 'Krytyczny',
    high: 'Wysoki',
    medium: 'Średni',
    low: 'Niski',
    found: 'Znaleziono',
    missing: 'Brak',
    auto_fix: 'auto-naprawa',

    // Category tags
    cat_content: 'Treść',
    cat_trust: 'Zaufanie',
    cat_ai: 'AI',
    cat_seo: 'SEO',
    cat_coverage: 'Pokrycie',
    cat_cro: 'Konwersja',
    cat_indexing: 'Indeksowanie',
    cat_contact: 'Kontakt',

    // Labels
    impact: 'Wpływ',
    effort: 'Trudność',
    acceptance_criteria: 'Kryteria Akceptacji',
    quick_wins: 'Szybkie Wygrane',
    next_30_days: 'Następne 30 Dni',
    priority_1: 'Priorytet 1 — Natychmiast, <1 tydzień',
    priority_2: 'Priorytet 2 — Krótkoterminowy, 1-2 tygodnie',
    priority_3: 'Priorytet 3 — Długoterminowy, 1+ miesiąc',

    // Buttons
    copy: 'Kopiuj',
    copied: 'Skopiowano',
    show_solution: 'Pokaż rozwiązanie',
    hide_code: 'Ukryj kod',
    how_to_deploy: 'Jak wdrożyć w 60 sekund',
    ai_prompt: 'Prompt AI',

    // GEO section
    ai_visibility_score: 'Wynik Widoczności AI',
    questions_answered: 'pytań z odpowiedzią',
    business_type: 'Typ działalności',
    ai_cannot_find: 'AI nie może znaleźć odpowiedzi na to pytanie klienta na Twojej stronie.',
    add_content_answering: 'Dodaj treść odpowiadającą na',
    why_ai_doesnt_cite: 'Dlaczego AI nie cytuje tej strony',
    ideal_keywords: 'Idealne Słowa Kluczowe',
    customer_questions: 'Pytania Klientów',

    // Sub-labels
    niche: 'Nisza',
    location: 'Lokalizacja',
    business_goal: 'Cel Biznesowy',
    primary_goal: 'Cel Podstawowy',
    business_model: 'Model Biznesowy',
    score_reasoning: 'Ocena Wyników',
    overall_assessment: 'Ogólna Ocena',
    top_performing_pages: 'Najlepsze Strony',
    problem_pages: 'Strony z Problemami',
    detected_elements: 'Wykryte Elementy',
    missing_elements: 'Brakujące Elementy',
    business_risk: 'Ryzyko Biznesowe',
    regional_assessment: 'Ocena Regionalna',
    on_page_assessment: 'Ocena Na Stronie',
    technical_assessment: 'Ocena Techniczna',
    structure_assessment: 'Ocena Struktury',
    keyword_positioning: 'Pozycjonowanie Słów Kluczowych',
    expected_visibility: 'Prognozowany Efekt Widoczności',
    internal_linking: 'Linkowanie Wewnętrzne',
    pages_scanned: 'przeskanowane strony',
    unique_pages: 'unikalne strony',
    full_audit: 'pełny audyt',
    pages: 'stron',

    // Fix types
    code_prompt_label: 'Prompt AI',
    code_html_label: 'HTML',
    code_text_label: 'Tekst',

    // Misc
    north_star: 'Główny Wskaźnik',
    additional_metrics: 'Dodatkowe Metryki',

    // Problem fix display
    show_fix: 'Pokaż poprawkę',
    hide_fix: 'Ukryj poprawkę',
    fix_available: 'Poprawka dostępna',
    unlock_with_paid: 'odblokuj z planem płatnym.',
    unlock: 'Odblokuj',
    code_label: 'kod',

    // Contact labels
    email_label: 'Email',
    social_label: 'Media społecznościowe',
    phone_label: 'Telefon',
    address_label: 'Adres',
    opening_hours: 'Godziny otwarcia',
    contact_form: 'Formularz kontaktowy dostępny',

    // Trust & AI labels
    expertise_signals: 'Sygnały ekspertyzy',
    weaknesses_detected: 'Wykryte słabości',
    trust_detected: 'Wykryte elementy zaufania',
    trust_missing: 'Brakujące elementy zaufania',
    missing_ai_signals: 'Brakujące sygnały AI',
    citability_level: 'Poziom cytowalności',
    schema_detected: 'Wykryte dane strukturalne',
    citability_strength: 'Siła cytowalności',
    geo_alignment: 'Zgodność GEO/LLM',
    business_opportunity: 'Szansa biznesowa',

    // Indexing labels
    sitemap_label: 'Mapa strony',
    robots_txt_label: 'Robots.txt',
    canonical_tags: 'Tagi Canonical',
    meta_robots: 'Meta Robots',
    present: 'Obecny',
    not_detected: 'Nie wykryto',
    ok_label: 'OK',
    issues_label: 'Problemy',
    no_blocking_meta: 'Nie wykryto blokujących tagów meta robots.',

    // Stats labels
    pages_crawled: 'przeskanowanych i przeanalizowanych stron',
    content_gaps: 'wykrytych luk w treści',
    issues_detected: 'wykrytych problemów',
    missing_contact: 'brakujących elementów kontaktowych',
    all_urls_analyzed: 'Wszystkie przeanalizowane URL-e',

    // Competitive
    competitive_analysis: 'Analiza Konkurencji',
    score_comparison: 'Porównanie Wyników',
    where_you_win: 'Gdzie Wygrywasz',
    where_competitors_lead: 'Gdzie Prowadzi Konkurencja',
    competitive_gaps: 'Luki Konkurencyjne',
    suggested_pages: 'Sugerowane Strony do Utworzenia',
  },

  de: {
    // Section titles
    executive_summary: 'Zusammenfassung',
    // Security & performance module
    security: 'Sicherheit',
    security_grade: 'Note',
    ssl_expires_in: 'SSL-Zertifikat läuft ab in',
    days: 'Tagen',
    security_issues: 'Zu behebende Sicherheitsprobleme',
    fix: 'Lösung',
    core_web_vitals: 'Core Web Vitals',
    cwv_desc: 'Reale Lade- und Interaktivitätsleistung, gemessen mit Google PageSpeed (mobil).',
    performance: 'Leistung',
    broken_links: 'Defekte Links',
    broken_links_found: 'Wir haben',
    broken_links_pages: 'defekte URL(s) gefunden',
    of_scanned: 'der gescannten Seiten',
    server_errors: 'Serverfehler (5xx)',
    business_overview: 'Geschäftsübersicht',
    coverage: 'Abdeckung',
    content: 'Inhalt',
    trust: 'Vertrauen',
    seo: 'SEO',
    indexing: 'Indexierung',
    ai_readiness: 'KI-Bereitschaft',
    contact: 'Kontakt',
    conversion_risk: 'Konversionsrisiko',
    action_plan: 'Aktionsplan',
    problems_and_fixes: 'Probleme & Lösungen',
    market_intelligence: 'Marktanalyse-Strategie',
    customer_questions_ai: 'Kundenfragen in KI',
    how_ai_sees_you: 'Wie KI Sie sieht',
    ready_to_deploy_briefs: 'Einsatzbereite Briefings',
    ready_to_deploy_brief: 'Einsatzbereites Briefing',
    kpi_tracking: 'KPI-Verfolgung',

    critical: 'Kritisch',
    high: 'Hoch',
    medium: 'Mittel',
    low: 'Niedrig',
    found: 'Gefunden',
    missing: 'Fehlt',
    auto_fix: 'Auto-Fix',

    cat_content: 'Inhalt',
    cat_trust: 'Vertrauen',
    cat_ai: 'KI',
    cat_seo: 'SEO',
    cat_coverage: 'Abdeckung',
    cat_cro: 'CRO',
    cat_indexing: 'Indexierung',
    cat_contact: 'Kontakt',

    impact: 'Auswirkung',
    effort: 'Aufwand',
    acceptance_criteria: 'Akzeptanzkriterien',
    quick_wins: 'Schnelle Erfolge',
    next_30_days: 'Nächste 30 Tage',
    priority_1: 'Priorität 1 — Sofort, <1 Woche',
    priority_2: 'Priorität 2 — Kurzfristig, 1-2 Wochen',
    priority_3: 'Priorität 3 — Langfristig, 1+ Monat',

    copy: 'Kopieren',
    copied: 'Kopiert',
    show_solution: 'Lösung anzeigen',
    hide_code: 'Code ausblenden',
    how_to_deploy: 'In 60 Sekunden bereitstellen',
    ai_prompt: 'KI-Prompt',

    ai_visibility_score: 'KI-Sichtbarkeitswert',
    questions_answered: 'Fragen beantwortet',
    business_type: 'Geschäftstyp',
    ai_cannot_find: 'KI kann keine Antwort auf diese Kundenfrage auf Ihrer Website finden.',
    add_content_answering: 'Inhalt hinzufügen, der beantwortet',
    why_ai_doesnt_cite: 'Warum KI diese Seite nicht zitiert',
    ideal_keywords: 'Ideale Schlüsselwörter',
    customer_questions: 'Kundenfragen',

    niche: 'Nische',
    location: 'Standort',
    business_goal: 'Geschäftsziel',
    primary_goal: 'Hauptziel',
    business_model: 'Geschäftsmodell',
    score_reasoning: 'Bewertungsbegründung',
    overall_assessment: 'Gesamtbewertung',
    top_performing_pages: 'Leistungsstärkste Seiten',
    problem_pages: 'Problemseiten',
    detected_elements: 'Erkannte Elemente',
    missing_elements: 'Fehlende Elemente',
    business_risk: 'Geschäftsrisiko',
    regional_assessment: 'Regionale Bewertung',
    on_page_assessment: 'On-Page Bewertung',
    technical_assessment: 'Technische Bewertung',
    structure_assessment: 'Strukturbewertung',
    keyword_positioning: 'Keyword-Positionierung',
    expected_visibility: 'Erwarteter Sichtbarkeitseffekt',
    internal_linking: 'Interne Verlinkung',
    pages_scanned: 'gescannte Seiten',
    unique_pages: 'einzigartige Seiten',
    full_audit: 'vollständiger Audit',
    pages: 'Seiten',

    code_prompt_label: 'KI-Prompt',
    code_html_label: 'HTML',
    code_text_label: 'Text',

    north_star: 'Leitstern-Metrik',
    additional_metrics: 'Zusätzliche Metriken',
  },
};

/**
 * Get translation function for a specific language.
 * Falls back to English if key not found.
 * @param {string} lang - Language code (en, pl, de, etc.)
 * @returns {function} t(key) -> translated string
 */
export function useTranslations(lang = 'en') {
  const dict = translations[lang] || translations.en;
  return (key, fallback) => dict[key] || translations.en[key] || fallback || key;
}

/**
 * Translate a severity/priority badge.
 */
export function translateSeverity(severity, lang = 'en') {
  const dict = translations[lang] || translations.en;
  const key = (severity || '').toLowerCase();
  return dict[key] || translations.en[key] || severity;
}

/**
 * Translate a category tag (Content, Trust, AI, etc.)
 */
export function translateCategory(category, lang = 'en') {
  const dict = translations[lang] || translations.en;
  const key = `cat_${(category || '').toLowerCase()}`;
  return dict[key] || translations.en[key] || category;
}

export default translations;
